import { useId, type ReactNode } from 'react';
import { WORKSPACE_TIMEZONES } from '@forecastify/shared';
import { ImageField, PrefixInput, Select, SetupHeading } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { CurrencyField, defaultCurrency } from './setup-currency';
import { SetupField } from './setup-field';
import { IMAGE_TYPES, addressFromName, imageProblem, typedAddress } from './setup-rules';
import type { AddressCheck } from './use-address-check';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Setup step 2 — the workspace: its name, logo, address and time zone.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The onboarding mockup's step 2. Submitting it CREATES the workspace (`0202`): before this step
 * there is none, for an invited admin and a self-serve one alike.
 *
 * ⚠️ **The address follows the name until somebody types in it** — the mockup's `slugTouched` —
 * so the common case is a name and Enter, and an address of one's own is one edit away.
 *
 * ⚠️ **The logo is held** and uploaded once the workspace exists, as `workspace-dialog.tsx` does.
 */
export interface WorkspaceValues {
  readonly name: string;
  readonly slug: string;
  readonly slugTouched: boolean;
  readonly logo: File | null;
  readonly timezone: (typeof WORKSPACE_TIMEZONES)[number];
  /** U-13 · the default currency, a `CURRENCY_CATALOGUE` code — `setup-currency.tsx`. */
  readonly currency: string;
}

/** The browser's own zone when it is one of the six, else Budapest — R-6's default. */
export function defaultTimezone(): WorkspaceValues['timezone'] {
  const own = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return WORKSPACE_TIMEZONES.find((zone) => zone === own) ?? 'Europe/Budapest';
}

export function workspaceValuesFrom(name: string): WorkspaceValues {
  return {
    name,
    slug: addressFromName(name),
    slugTouched: false,
    logo: null,
    timezone: defaultTimezone(),
    currency: defaultCurrency(),
  };
}

/** Why the step cannot go on yet, or `null` — the mockup's `blockedKey` for step 2. */
export function workspaceBlocked(
  values: WorkspaceValues,
  check: AddressCheck,
  t: Translate,
): string | null {
  if (values.name.trim() === '') return t('setup.workspace.blockedName');
  if (values.slug === '') return t('setup.workspace.blockedSlug');
  if (check.state === 'checking' || check.state === 'idle')
    return t('setup.workspace.blockedCheck');
  if (check.state !== 'ok') return t('setup.workspace.blockedTaken');
  return null;
}

/** `GMT+2` for a zone, today — computed rather than written, so summer time is right. */
function offsetOf(zone: string): string {
  const part = new Intl.DateTimeFormat('en', { timeZone: zone, timeZoneName: 'shortOffset' })
    .formatToParts(new Date())
    .find((piece) => piece.type === 'timeZoneName');
  return part?.value ?? '';
}

export function WorkspaceStep({
  values,
  onChange,
  check,
  logoSrc,
  logoError,
  onLogoError,
  addressPrefix,
}: {
  readonly values: WorkspaceValues;
  readonly onChange: (next: WorkspaceValues) => void;
  readonly check: AddressCheck;
  readonly logoSrc: string | null;
  readonly logoError: string | null;
  readonly onLogoError: (message: string | null) => void;
  /** `app.forecastify.hu/` — the host the address will live under. */
  readonly addressPrefix: string;
}): ReactNode {
  const t = useT();
  const slugId = useId();

  const message =
    check.state === 'checking'
      ? t('setup.workspace.checking')
      : check.state === 'ok'
        ? t('setup.workspace.available')
        : check.state === 'taken'
          ? t('setup.workspace.taken', { slug: values.slug })
          : check.state === 'reserved'
            ? t('setup.workspace.reserved', { slug: values.slug })
            : check.state === 'invalid'
              ? check.problem === 'chars'
                ? t('setup.workspace.errChars')
                : check.problem === 'hyphen'
                  ? t('setup.workspace.errHyphen')
                  : t('setup.workspace.errShort')
              : '';
  const suggestion =
    check.state === 'taken' || check.state === 'reserved' || check.state === 'invalid'
      ? check.suggestion
      : null;

  return (
    <>
      <SetupHeading title={t('setup.workspace.title')} subtitle={t('setup.workspace.subtitle')} />
      <div className="flex flex-col gap-5">
        <ImageField
          shape="rounded"
          src={logoSrc}
          initials={(values.name.trim()[0] ?? 'W').toUpperCase()}
          accept={IMAGE_TYPES.join(',')}
          labels={{
            upload: t('setup.upload'),
            replace: t('setup.replace'),
            remove: t('setup.remove'),
          }}
          hint={t('setup.workspace.logoHint')}
          error={logoError}
          onPick={(file) => {
            const problem = imageProblem(file);
            if (problem !== null) {
              onLogoError(
                problem === 'type' ? t('setup.workspace.errType') : t('setup.workspace.errSize'),
              );
              return;
            }
            onLogoError(null);
            onChange({ ...values, logo: file });
          }}
          onRemove={() => {
            onChange({ ...values, logo: null });
          }}
        >
          <SetupField
            label={t('setup.workspace.name')}
            placeholder={t('setup.workspace.namePlaceholder')}
            value={values.name}
            autoComplete="organization"
            aria-required="true"
            autoFocus
            onChange={(event) => {
              const name = event.target.value;
              onChange(
                values.slugTouched
                  ? { ...values, name }
                  : { ...values, name, slug: addressFromName(name) },
              );
            }}
          />
        </ImageField>

        <PrefixInput
          id={slugId}
          label={t('setup.workspace.address')}
          prefix={addressPrefix}
          value={values.slug}
          onChange={(raw) => {
            onChange({ ...values, slug: typedAddress(raw), slugTouched: true });
          }}
          state={
            check.state === 'checking'
              ? 'checking'
              : check.state === 'ok'
                ? 'ok'
                : check.state === 'idle'
                  ? 'idle'
                  : 'bad'
          }
          message={message}
          suggestion={
            suggestion === null
              ? null
              : {
                  lead: t('setup.workspace.suggest'),
                  value: suggestion,
                  onUse: () => {
                    onChange({ ...values, slug: suggestion, slugTouched: true });
                  },
                }
          }
        />

        <div className="flex flex-col gap-1.5">
          <span className="text-muted-foreground text-[12px] font-medium">
            {t('setup.workspace.timezone')}
          </span>
          <Select
            aria-label={t('setup.workspace.timezone')}
            icon="globe"
            value={values.timezone}
            options={WORKSPACE_TIMEZONES.map((zone) => ({
              value: zone,
              label: zone,
              hint: offsetOf(zone),
            }))}
            onChange={(event) => {
              const zone = WORKSPACE_TIMEZONES.find((entry) => entry === event.target.value);
              if (zone !== undefined) onChange({ ...values, timezone: zone });
            }}
          />
        </div>

        <CurrencyField
          value={values.currency}
          onChange={(currency) => {
            onChange({ ...values, currency });
          }}
        />
      </div>
    </>
  );
}
